
import React, { useEffect, useState, useMemo } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { CheckCircle2, AlertCircle, ShoppingBag, Banknote, Calendar as CalendarIcon, LayoutList, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';

const PHASES = [
  { key: 'early', months: '12–9' },
  { key: 'mid', months: '9–6' },
  { key: 'approaching', months: '6–3' },
  { key: 'final', months: '3–1' },
  { key: 'last_week', months: 'Poslední týden' },
  { key: 'today', months: 'Svatební den' }
];

type ViewMode = 'timeline' | 'monthly';

export default function Timeline() {
  const { wedding, locale } = useWedding();
  const { weddingId } = useParams();
  const t = (key: string) => getTranslation(locale, key);
  const navigate = useNavigate();

  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem('preparation_view_mode') as ViewMode) || 'monthly';
  });

  // Persist view mode
  useEffect(() => {
    localStorage.setItem('preparation_view_mode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!wedding) return;
    const fetchData = async () => {
      const supabase = getAuthenticatedSupabase();
      const [tRes, vRes, bRes] = await Promise.all([
        supabase.from('tasks').select('*').eq('wedding_id', wedding.id),
        supabase.from('vendors').select('*').eq('wedding_id', wedding.id),
        supabase.from('budget_items').select('*').eq('wedding_id', wedding.id)
      ]);

      const all = [
        ...(tRes.data || []).map(x => ({ ...x, type: 'task' })),
        ...(vRes.data || []).map(x => ({ ...x, type: 'vendor', title: `Zajistit: ${x.name}`, phase: 'mid', status: x.status === 'booked' ? 'done' : 'todo' })),
        ...(bRes.data || []).filter(x => x.paid_amount < x.actual_amount && x.actual_amount > 0).map(x => ({ ...x, type: 'budget', title: `Zaplatit: ${x.title}`, phase: 'final', status: 'todo' }))
      ];

      setItems(all);
      setLoading(false);
    };
    fetchData();
  }, [wedding]);

  const handleNavigate = (type: string) => {
    navigate(`/app/w/${weddingId}/${type}s`);
  };

  // Generate full range of months from min to max date
  const timelineMonths = useMemo(() => {
    if (!wedding) return [];
    
    // Find min date (either today or first task) and max date (wedding date)
    const today = new Date();
    const weddingDate = new Date(wedding.date);
    
    const taskDates = items
      .filter(i => i.due_date)
      .map(i => new Date(i.due_date).getTime());
      
    // Include 1 year before wedding if no earlier tasks
    const minTime = taskDates.length > 0 
      ? Math.min(...taskDates, today.getTime()) 
      : new Date(weddingDate).setFullYear(weddingDate.getFullYear() - 1);

    const minDate = new Date(minTime);
    const maxDate = new Date(weddingDate.getFullYear(), weddingDate.getMonth() + 1, 1); // 1 month after wedding

    // Normalize to start of month
    const start = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    
    const months: Date[] = [];
    const curr = new Date(start);
    
    while (curr <= maxDate) {
      months.push(new Date(curr));
      curr.setMonth(curr.getMonth() + 1);
    }
    return months;
  }, [items, wedding]);

  // Group items by YYYY-MM
  const itemsByMonth = useMemo(() => {
    const groups: Record<string, any[]> = {};
    items.forEach(item => {
      if (!item.due_date) return;
      const date = new Date(item.due_date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  }, [items]);

  if (!wedding) return null;

  const total = items.length;
  const done = items.filter(i => i.status === 'done').length;
  const progress = total ? Math.round((done / total) * 100) : 0;
  
  const weddingDate = new Date(wedding.date);
  const diff = weddingDate.getTime() - new Date().getTime();
  const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));

  const isMonthCurrent = (d: Date) => {
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl mx-auto pb-24">
      {/* Header Summary - More Compact */}
      <div className="bg-white dark:bg-slate-900 px-8 py-6 rounded-2xl border border-ds-border relative overflow-hidden flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="relative z-10 text-center md:text-left">
          <h1 className="text-ds-h1 text-slate-900 dark:text-white mb-1">{t('timeline.title')}</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {diffDays > 0 ? `${diffDays} ${t('common.days_to_go')}` : t('common.passed')}
          </p>
        </div>
        <div className="relative z-10 w-full md:w-64 space-y-2">
          <div className="flex justify-between text-ds-meta text-slate-600 dark:text-slate-400">
            <span>{t('timeline.progress')}</span>
            <span className="text-brand-600 dark:text-brand-400 font-semibold">{progress}%</span>
          </div>
          <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-600 transition-all duration-1000" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* View Switcher */}
      <div className="flex justify-center sticky top-20 z-30">
        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-1 rounded-xl flex gap-1 border border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setViewMode('monthly')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
              viewMode === 'monthly' 
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' 
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <CalendarIcon size={14} /> Přehled po měsících
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
              viewMode === 'timeline' 
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white' 
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            <LayoutList size={14} /> Fáze
          </button>
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'timeline' ? (
        <div className="space-y-12 relative before:absolute before:inset-y-0 before:left-5 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
          {PHASES.map((phase) => {
            const phaseItems = items.filter(i => i.phase === phase.key);
            if (phaseItems.length === 0 && phase.key !== 'early') return null;

            return (
              <div key={phase.key} className="relative pl-14">
                {/* Marker */}
                <div className="absolute left-0 top-0 w-10 h-10 bg-white dark:bg-[#121215] border border-ds-border rounded-xl flex items-center justify-center z-10">
                  <Clock className="text-slate-300 dark:text-slate-700" size={16} />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <h3 className="text-ds-meta text-slate-400">{t(`timeline.phases.${phase.key}`)}</h3>
                    <div className="flex-1 h-px bg-slate-100 dark:bg-slate-800" />
                  </div>

                  <div className="grid gap-2">
                    {phaseItems.map((item: any) => (
                      <div 
                        key={item.id} 
                        onClick={() => handleNavigate(item.type)}
                        className="bg-white dark:bg-[#121215] p-4 rounded-xl border border-ds-border transition-all cursor-pointer group flex items-center justify-between"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${
                            item.status === 'done' ? 'bg-green-50 dark:bg-green-900/10 text-green-500' : 
                            (item.due_date && new Date(item.due_date) < new Date()) ? 'bg-red-50 dark:bg-red-900/10 text-red-500' : 
                            'bg-slate-50 dark:bg-slate-800 text-slate-400'
                          }`}>
                            {item.type === 'task' ? <CheckCircle2 size={16} /> : item.type === 'vendor' ? <ShoppingBag size={16} /> : <Banknote size={16} />}
                          </div>
                          <div>
                            <p className={`font-bold text-sm transition-all ${item.status === 'done' ? 'text-slate-300 dark:text-slate-700 line-through' : 'text-slate-900 dark:text-white'}`}>
                              {item.title}
                            </p>
                            <span className="text-ds-meta text-slate-400">
                              {item.due_date ? new Date(item.due_date).toLocaleDateString(locale) : t('common.none')}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="text-slate-200 dark:text-slate-800 group-hover:text-brand-500 transition-colors" size={16} />
                      </div>
                    ))}
                    {phaseItems.length === 0 && <p className="text-xs text-slate-300 italic py-2">Žádné úkoly v této fázi.</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="relative py-4">
          <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-800" />
          
          <div className="space-y-1">
            {timelineMonths.map((date, idx) => {
              const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
              const monthItems = itemsByMonth[key] || [];
              const isCurrent = isMonthCurrent(date);
              
              return (
                <TimelineMonthNode 
                  key={key} 
                  date={date}
                  items={monthItems} 
                  locale={locale} 
                  onNavigate={handleNavigate}
                  isCurrent={isCurrent}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact Monthly Timeline Node
interface TimelineMonthProps {
  date: Date;
  items: any[];
  locale: string;
  onNavigate: (type: string) => void;
  isCurrent: boolean;
}

const TimelineMonthNode: React.FC<TimelineMonthProps> = ({ date, items, locale, onNavigate, isCurrent }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isEmpty = items.length === 0;
  
  const monthName = new Intl.DateTimeFormat(locale === 'cs' ? 'cs-CZ' : 'en-US', { month: 'long' }).format(date);
  const year = date.getFullYear();
  
  const total = items.length;
  const done = items.filter(i => i.status === 'done').length;
  const overdue = items.filter(i => i.status !== 'done' && new Date(i.due_date) < new Date()).length;
  const percentage = total > 0 ? Math.round((done / total) * 100) : 0;

  // Progress Color Logic
  let progressColor = 'bg-red-500';
  if (percentage >= 80) progressColor = 'bg-green-500';
  else if (percentage >= 40) progressColor = 'bg-orange-500';

  return (
    <div className="relative pl-8 group">
        {/* Timeline Dot */}
        <div className={`
            absolute left-[5px] top-4 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-black z-10 transition-colors duration-300
            ${isCurrent ? 'bg-brand-500 ring-4 ring-brand-100 dark:ring-brand-900/30' : isEmpty ? 'bg-slate-300 dark:bg-slate-700' : 'bg-slate-900 dark:bg-slate-400'}
        `} />

        {/* Row Container */}
        <div className={`
            rounded-xl transition-all duration-200 border
            ${isEmpty 
                ? 'border-transparent py-2' 
                : 'bg-white dark:bg-[#121215] border-ds-border hover:border-brand-200 dark:hover:border-slate-700 mb-2'
            }
        `}>
            {/* Header */}
            <div 
                onClick={() => !isEmpty && setIsExpanded(!isExpanded)}
                className={`flex items-center gap-4 px-4 py-2 ${!isEmpty ? 'cursor-pointer' : ''}`}
            >
                {/* Month Name */}
                <div className="w-28 shrink-0">
                    <h3 className={`text-sm font-semibold capitalize ${isEmpty ? 'text-slate-400 font-normal' : 'text-slate-900 dark:text-white'}`}>
                        {monthName} <span className={`font-normal ${isEmpty ? 'text-slate-300' : 'text-slate-400'}`}>{year}</span>
                    </h3>
                </div>

                {/* Content */}
                <div className="flex-1 flex items-center gap-4 min-w-0">
                    {isEmpty ? (
                        <span className="text-xs text-slate-300 dark:text-slate-700 font-medium select-none">Žádné plánované úkoly</span>
                    ) : (
                        <>
                            {/* Compact Progress Bar */}
                            <div className="hidden sm:block flex-1 max-w-[120px] h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                <div className={`h-full ${progressColor} transition-all duration-500`} style={{ width: `${percentage}%` }} />
                            </div>
                            
                            {/* Stats Badges */}
                            <div className="flex items-center gap-2 text-[10px] font-medium">
                                <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-400">
                                    {done}/{total}
                                </span>
                                {overdue > 0 && (
                                    <span className="text-red-500 flex items-center gap-1 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded">
                                        <AlertCircle size={10} /> {overdue}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Chevron */}
                {!isEmpty && (
                    <div className={`text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown size={14} />
                    </div>
                )}
            </div>

            {/* Expanded Tasks */}
            {isExpanded && !isEmpty && (
                <div className="border-t border-slate-50 dark:border-slate-800 px-2 py-2 space-y-0.5 bg-slate-50/50 dark:bg-slate-900/20 rounded-b-xl">
                    {items.map(item => (
                        <div 
                            key={item.id}
                            onClick={(e) => { e.stopPropagation(); onNavigate(item.type); }}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-800 cursor-pointer transition-colors group/item"
                        >
                            <div className={`
                                w-3 h-3 rounded-full border shrink-0 flex items-center justify-center
                                ${item.status === 'done' ? 'bg-green-500 border-green-500' : 'border-slate-300 dark:border-slate-600'}
                            `}>
                                {item.status === 'done' && <CheckCircle2 size={8} className="text-white" />}
                            </div>
                            
                            <span className={`text-xs font-medium truncate flex-1 ${item.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-700 dark:text-slate-300'}`}>
                                {item.title}
                            </span>
                            
                            <span className="text-[10px] text-slate-400 shrink-0 tabular-nums">
                                {new Date(item.due_date).getDate()}.
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    </div>
  );
};
